import { useState, useEffect, useCallback } from 'react';
import teamService from '../../../utils/teamService.js';
import apiService from '../../../utils/apiService.js';

export const useTeamMembers = (organizationId, currentUser, userRole, searchQuery, roleFilter) => {
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivityStatus = useCallback(async (orgId) => {
    try {
      const realApiService = (await import('../../../utils/realApiService.js')).default;
      const response = await realApiService.users.getActivityStatus(orgId);
      if (response && response.data) {
        const statusMap = new Map();
        response.data.forEach(userActivity => {
          if (userActivity.user_id) statusMap.set(userActivity.user_id, userActivity);
        });
        return statusMap;
      }
    } catch (err) {
      console.error('Failed to fetch activity status:', err);
    }
    return new Map();
  }, []);

  const loadTeamData = useCallback(async () => {
    if (!organizationId) return;

    try {
      setIsLoading(true);
      const activityMap = await fetchActivityStatus(organizationId);

      // Fetch data in parallel
      const [activeResult, pendingResult, requestsResult] = await Promise.allSettled([
        teamService.getTeamMembers(organizationId, { search: searchQuery, role: roleFilter !== 'all' ? roleFilter : undefined }),
        teamService.getPendingInvitations(organizationId),
        apiService.organizations.getJoinRequests(organizationId)
      ]);

      const activeMembers = activeResult.status === 'fulfilled' ? activeResult.value : [];
      const pendingInvitations = pendingResult.status === 'fulfilled' ? pendingResult.value : [];
      const requests = requestsResult.status === 'fulfilled' ? requestsResult.value : [];

      setJoinRequests(requests || []);

      // Normalize and combine
      const activeMemberEmails = new Set(activeMembers.map(m => m.email));
      const filteredPending = pendingInvitations.filter(i => !activeMemberEmails.has(i.email));

      const normalizedMembers = [
        ...activeMembers.map(m => {
          const userId = m.user_id || m.id;
          const activity = activityMap.get(userId) || { status: 'offline', is_active: false };
          return {
            ...m,
            status: activity.status || (activity.is_active ? 'online' : 'offline'),
            lastActivity: activity.last_activity || m.lastActivity || m.joinedDate,
            name: m.name || `${m.user?.first_name || ''} ${m.user?.last_name || ''}`.trim() || m.email?.split('@')[0] || 'Unknown User',
            email: m.email || m.user?.email || 'unknown@example.com'
          };
        }),
        ...filteredPending.map(p => ({
          id: p.id,
          email: p.email || 'unknown@example.com',
          name: (p.email || 'unknown@example.com').split('@')[0],
          role: p.role || 'member',
          status: 'pending',
          joinedAt: p.created_at,
          lastActivity: p.created_at
        }))
      ];

      // If current user is missing, add as fallback
      if (normalizedMembers.length === 0 && currentUser) {
        normalizedMembers.push({
          id: currentUser.id || 'current-user',
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email?.split('@')[0] || 'Me',
          email: currentUser.email,
          role: userRole || 'member',
          status: 'online',
          joinedDate: new Date().toISOString()
        });
      }

      setMembers(normalizedMembers.sort((a, b) => {
        const order = { active: 0, accepted: 1, pending: 2 };
        return (order[a.status] || 0) - (order[b.status] || 0);
      }));
      setError(null);
    } catch (err) {
      console.error('Failed to load team data:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, currentUser, userRole, searchQuery, roleFilter, fetchActivityStatus]);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  return { members, joinRequests, isLoading, error, refresh: loadTeamData };
};
